/**
 * About Us template — renderer + editor + default data.
 * Used in PagesAdmin (editor) and StorefrontPage (renderer).
 */

import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

// ── Default template data ──────────────────────────────────────────────────
export const ABOUT_US_DEFAULT = {
  hero: {
    eyebrow:   "Welcome to IMPORTREND",
    headline:  "About Importrend",
    hl_accent: "Importrend",
    subheadline: "Bringing Useful Products to Your Everyday Life",
    lead: "At Importrend, we are passionate about making quality products more accessible. We bring together practical everyday essentials, lifestyle finds and useful innovations designed to add convenience and value to your day.",
    badges: ["✦ Quality Products", "🚚 Reliable Delivery", "🛒 Wide Selection", "♡ Customer First"],
    art_text: "IMPORTREND",
    float1: "✓ Carefully selected",
    float2: "📦 Everyday essentials",
  },
  story: {
    eyebrow: "Who we are",
    headline: "Our Story",
    hl_accent: "Story",
    para1: "Importrend began with a simple idea: make useful, quality products easier to discover and buy. We focus on bringing practical and appealing products to customers across India.",
    para2: "From everyday home essentials to lifestyle accessories, we aim to make shopping straightforward, dependable and worthwhile.",
    emoji: "🌏📦",
  },
  what_we_do: {
    eyebrow: "Our products",
    headline: "What We Do",
    hl_accent: "We Do",
    para1: "We offer a selection of products across home essentials, personal care, fashion accessories and lifestyle categories. Our goal is to combine practical utility, variety and value in one easy-to-shop destination.",
    para2: "We continually explore new products that can make everyday routines simpler and more enjoyable.",
    emoji: "🛍️",
  },
  why_choose: {
    eyebrow: "Our difference",
    headline: "Why Choose Importrend?",
    hl_accent: "Importrend?",
    subtext: "Thoughtful product choices and a customer-focused shopping experience.",
    cards: [
      { icon: "🛡️", title: "Carefully Selected Products", text: "Useful, practical finds for everyday needs.", variant: "story-card" },
      { icon: "💎", title: "Great Value",                  text: "Products selected with affordability in mind.", variant: "blue" },
      { icon: "🚚", title: "Reliable Shipping",            text: "Clear order handling and delivery information.", variant: "green" },
      { icon: "🎧", title: "Customer Support",             text: "Help with orders, products and shopping questions.", variant: "pink" },
    ],
  },
  mvv: {
    eyebrow: "What guides us",
    headline: "Our Mission, Vision & Values",
    hl_accent: "Vision & Values",
    cards: [
      { icon: "🎯", title: "Our Mission", text: "To make quality and innovative everyday products more accessible to households across India.", variant: "story-card" },
      { icon: "👁️", title: "Our Vision",  text: "To become a trusted destination for useful everyday essentials and lifestyle products.",    variant: "blue" },
      { icon: "🤝", title: "Our Values",  text: "Customer satisfaction, quality, reliability, fair business practices and continuous improvement.", variant: "green" },
    ],
  },
  commitment: {
    eyebrow: "Our promise",
    headline: "Our Commitment",
    hl_accent: "Commitment",
    text: "We strive to provide a smooth shopping experience through clear product information, careful order handling and responsive customer service. We listen to feedback and keep improving our selection and processes.",
    emoji: "📦✨",
  },
  cta: {
    headline:     "Discover Products for Everyday Living",
    hl_accent:    "Everyday Living",
    subtext:      "Explore home, lifestyle and personal-care essentials at IMPORTREND.",
    button_text:  "Explore Products →",
    button_url:   "/collections/all",
  },
};

// ── CSS scoped to the about-us renderer ───────────────────────────────────
const AU_CSS = `
.au-root{--ink:#221512;--orange:#db4b0c;--orange2:#ff853b;--cream:#fff8f1;--muted:#655b57;--line:#f0e4db;color:var(--ink);background:#fff;font-family:Inter,ui-sans-serif,system-ui,-apple-system,"Segoe UI",Arial,sans-serif;line-height:1.55}
.au-root *{box-sizing:border-box}
.au-root h1,.au-root h2,.au-root h3,.au-root p{margin-top:0}
.au-root h1{font-size:clamp(2.2rem,4vw,4.4rem);line-height:1.05;letter-spacing:-.04em;margin-bottom:18px}
.au-root h2{font-size:clamp(1.6rem,2.5vw,2.6rem);letter-spacing:-.03em;line-height:1.15;margin-bottom:18px}
.au-root h3{font-size:1.18rem;line-height:1.25;margin-bottom:10px}
.au-root p{color:var(--muted);margin-bottom:1rem}
.au-orange{color:var(--orange)}
.au-wrap{width:min(1200px,calc(100% - 44px));margin:auto}
.au-hero{background:radial-gradient(circle at 90% 10%,#ffdfc2 0,transparent 35%),linear-gradient(125deg,#fffaf5,#fff0e5);padding:74px 0 64px;overflow:hidden}
.au-hero-grid{display:grid;grid-template-columns:1.08fr .92fr;gap:46px;align-items:center}
.au-eyebrow{font-size:.77rem;letter-spacing:.18em;text-transform:uppercase;font-weight:800;color:var(--orange);margin-bottom:14px}
.au-lead{font-size:clamp(.95rem,1.4vw,1.12rem);max-width:600px}
.au-hero-art{position:relative;min-height:360px;display:grid;place-items:center}
.au-halo{position:absolute;width:min(90%,390px);aspect-ratio:1;border-radius:50%;background:linear-gradient(145deg,#ffe3cb,#ffb577);box-shadow:0 22px 65px #b85b2424}
.au-box{position:relative;width:260px;height:190px;background:linear-gradient(130deg,#d99a61,#b76e36);border-radius:8px;box-shadow:22px 28px 36px #77380b3b;transform:rotate(-4deg);display:grid;place-items:center;font-size:1.6rem;letter-spacing:.04em;font-weight:900;color:#3b2113}
.au-box:before{content:"";position:absolute;top:-40px;left:0;width:100%;height:41px;background:#e8ae79;clip-path:polygon(12% 0,100% 0,100% 100%,0 100%)}
.au-floating{position:absolute;background:white;border-radius:18px;box-shadow:0 15px 30px #9b57201c;padding:11px 16px;font-weight:700;font-size:.88rem}
.au-f1{top:28px;right:0}.au-f2{bottom:16px;left:0}
.au-badge-row{display:flex;gap:10px;flex-wrap:wrap;margin-top:22px}
.au-badge{background:#fff;border:1px solid #f5d9c8;padding:9px 14px;border-radius:100px;font-size:.85rem;font-weight:700}
.au-section{padding:64px 0}
.au-section.au-soft{background:#fff8f3}
.au-sh{text-align:center;max-width:700px;margin:0 auto 36px}
.au-grid4{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:18px}
.au-grid3{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:22px}
.au-card{border-radius:22px;background:#fff;padding:28px;border:1px solid var(--line);box-shadow:0 10px 30px #6d381008}
.au-card p:last-child{margin-bottom:0}
.au-story{background:linear-gradient(125deg,#fff2e8,#fff)}.au-blue{background:#eff8ff}.au-green{background:#effaf2}.au-pink{background:#fff1f2}
.au-icon{width:56px;height:56px;border-radius:18px;background:#ffe2d1;display:grid;place-items:center;font-size:1.65rem;margin-bottom:16px}
.au-blue .au-icon{background:#d9efff}.au-green .au-icon{background:#d9f4e0}.au-pink .au-icon{background:#ffdee5}
.au-feature{text-align:center}.au-feature .au-icon{margin:0 auto 16px}.au-feature p{font-size:.93rem}
.au-split{display:grid;grid-template-columns:1fr 1fr;align-items:center;gap:40px}
.au-visual{min-height:290px;border-radius:28px;background:linear-gradient(135deg,#ffe2c6,#f7ad72);display:grid;place-items:center;position:relative;overflow:hidden}
.au-visual:before{content:"";width:230px;height:230px;border:28px solid #ffffff88;border-radius:50%;position:absolute;right:-65px;top:-80px}
.au-visual .au-emoji{font-size:clamp(6rem,12vw,10rem);filter:drop-shadow(0 18px 10px #78432333);z-index:1}
.au-visual.au-alt{background:linear-gradient(135deg,#e6f6ed,#c8e9d5)}
.au-commit{background:linear-gradient(110deg,#fff2e7,#ffe1ca);border-radius:26px;padding:42px;display:grid;grid-template-columns:1.4fr .6fr;align-items:center;gap:20px}
.au-commit-emoji{font-size:7rem;text-align:center}
.au-cta{background:#2c1a15;color:#fff;text-align:center;padding:60px 0}
.au-cta p{color:#ead6ca}
.au-btn{display:inline-block;text-decoration:none;border-radius:999px;background:var(--orange);color:#fff;padding:13px 24px;font-weight:800}
@media(max-width:900px){.au-hero-grid,.au-split{grid-template-columns:1fr}.au-hero-art{min-height:290px}.au-grid4{grid-template-columns:repeat(2,1fr)}.au-grid3{grid-template-columns:1fr 1fr}.au-split .au-visual{order:-1}.au-commit{grid-template-columns:1fr}.au-commit-emoji{display:none}}
@media(max-width:600px){.au-wrap{width:min(100% - 28px,520px)}.au-hero{padding:40px 0}.au-hero-grid{gap:10px}.au-hero-art{min-height:250px}.au-section{padding:44px 0}.au-grid3,.au-grid4{grid-template-columns:1fr}.au-card{padding:22px}.au-commit{padding:26px}}
`;

function accentify(text, accent) {
  if (!accent || !text.includes(accent)) return <span>{text}</span>;
  const parts = text.split(accent);
  return (
    <span>
      {parts[0]}<span className="au-orange">{accent}</span>{parts.slice(1).join(accent)}
    </span>
  );
}

// ── Renderer ───────────────────────────────────────────────────────────────
export function AboutUsRenderer({ data }) {
  const d = data || ABOUT_US_DEFAULT;
  const h = d.hero       || ABOUT_US_DEFAULT.hero;
  const s = d.story      || ABOUT_US_DEFAULT.story;
  const w = d.what_we_do || ABOUT_US_DEFAULT.what_we_do;
  const y = d.why_choose || ABOUT_US_DEFAULT.why_choose;
  const m = d.mvv        || ABOUT_US_DEFAULT.mvv;
  const c = d.commitment || ABOUT_US_DEFAULT.commitment;
  const t = d.cta        || ABOUT_US_DEFAULT.cta;

  const variantClass = (v) => ({ "story-card": "au-story", blue: "au-blue", green: "au-green", pink: "au-pink" }[v] || "");

  return (
    <div className="au-root">
      <style>{AU_CSS}</style>

      {/* Hero */}
      <section className="au-hero">
        <div className="au-wrap au-hero-grid">
          <div>
            <div className="au-eyebrow">{h.eyebrow}</div>
            <h1>{accentify(h.headline, h.hl_accent)}</h1>
            <h3>{h.subheadline}</h3>
            <p className="au-lead">{h.lead}</p>
            <div className="au-badge-row">
              {(h.badges || []).map((b, i) => <span key={i} className="au-badge">{b}</span>)}
            </div>
          </div>
          <div className="au-hero-art">
            <div className="au-halo" />
            <div className="au-box">{h.art_text}</div>
            <div className="au-floating au-f1">{h.float1}</div>
            <div className="au-floating au-f2">{h.float2}</div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="au-section">
        <div className="au-wrap au-split">
          <div>
            <div className="au-eyebrow">{s.eyebrow}</div>
            <h2>{accentify(s.headline, s.hl_accent)}</h2>
            <p>{s.para1}</p>
            <p>{s.para2}</p>
          </div>
          <div className="au-visual"><div className="au-emoji">{s.emoji}</div></div>
        </div>
      </section>

      {/* What We Do */}
      <section className="au-section au-soft">
        <div className="au-wrap au-split">
          <div className="au-visual au-alt"><div className="au-emoji">{w.emoji}</div></div>
          <div>
            <div className="au-eyebrow">{w.eyebrow}</div>
            <h2>{accentify(w.headline, w.hl_accent)}</h2>
            <p>{w.para1}</p>
            <p>{w.para2}</p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="au-section">
        <div className="au-wrap">
          <div className="au-sh">
            <div className="au-eyebrow">{y.eyebrow}</div>
            <h2>{accentify(y.headline, y.hl_accent)}</h2>
            <p>{y.subtext}</p>
          </div>
          <div className="au-grid4">
            {(y.cards || []).map((card, i) => (
              <article key={i} className={`au-card au-feature ${variantClass(card.variant)}`}>
                <div className="au-icon">{card.icon}</div>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="au-section au-soft">
        <div className="au-wrap">
          <div className="au-sh">
            <div className="au-eyebrow">{m.eyebrow}</div>
            <h2>{accentify(m.headline, m.hl_accent)}</h2>
          </div>
          <div className="au-grid3">
            {(m.cards || []).map((card, i) => (
              <article key={i} className={`au-card ${variantClass(card.variant)}`}>
                <div className="au-icon">{card.icon}</div>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="au-section">
        <div className="au-wrap">
          <div className="au-commit">
            <div>
              <div className="au-eyebrow">{c.eyebrow}</div>
              <h2>{accentify(c.headline, c.hl_accent)}</h2>
              <p>{c.text}</p>
            </div>
            <div className="au-commit-emoji">{c.emoji}</div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="au-cta">
        <div className="au-wrap">
          <h2>{accentify(t.headline, t.hl_accent)}</h2>
          <p>{t.subtext}</p>
          <a className="au-btn" href={t.button_url}>{t.button_text}</a>
        </div>
      </section>
    </div>
  );
}

// ── Editor helpers ─────────────────────────────────────────────────────────
function Field({ label, value, onChange, multiline = false, hint }) {
  const cls = "w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white resize-none";
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {multiline
        ? <textarea rows={3} value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />
        : <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} className={cls} />}
      {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <span className="text-xs font-bold text-slate-700">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 py-4 space-y-3 bg-white">{children}</div>}
    </div>
  );
}

// ── Editor ─────────────────────────────────────────────────────────────────
export function AboutUsEditor({ data, onChange }) {
  const d = data || ABOUT_US_DEFAULT;

  const set = (section, field, value) => {
    onChange({ ...d, [section]: { ...(d[section] || {}), [field]: value } });
  };
  const setCard = (section, cardIdx, field, value) => {
    const cards = [...((d[section] || {}).cards || [])];
    cards[cardIdx] = { ...cards[cardIdx], [field]: value };
    onChange({ ...d, [section]: { ...(d[section] || {}), cards } });
  };
  const addCard = (section, defaults) => {
    const cards = [...((d[section] || {}).cards || []), defaults];
    onChange({ ...d, [section]: { ...(d[section] || {}), cards } });
  };
  const removeCard = (section, idx) => {
    const cards = ((d[section] || {}).cards || []).filter((_, i) => i !== idx);
    onChange({ ...d, [section]: { ...(d[section] || {}), cards } });
  };
  const setBadge = (idx, value) => {
    const badges = [...((d.hero || {}).badges || [])];
    badges[idx] = value;
    onChange({ ...d, hero: { ...(d.hero || {}), badges } });
  };
  const addBadge = () => {
    const badges = [...((d.hero || {}).badges || []), "New Badge"];
    onChange({ ...d, hero: { ...(d.hero || {}), badges } });
  };
  const removeBadge = (idx) => {
    const badges = ((d.hero || {}).badges || []).filter((_, i) => i !== idx);
    onChange({ ...d, hero: { ...(d.hero || {}), badges } });
  };

  const h = d.hero       || {};
  const s = d.story      || {};
  const w = d.what_we_do || {};
  const y = d.why_choose || {};
  const m = d.mvv        || {};
  const c = d.commitment || {};
  const t = d.cta        || {};

  return (
    <div className="space-y-3">
      <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-xs text-orange-700">
        <strong>About Us Template</strong> — Edit each section below. The <em>Accent word</em> field colours that exact word orange in the heading.
      </div>

      {/* Hero */}
      <Section title="1. Hero Section" defaultOpen>
        <Field label="Eyebrow text"    value={h.eyebrow}      onChange={v => set("hero","eyebrow",v)} />
        <Field label="Headline"        value={h.headline}     onChange={v => set("hero","headline",v)} />
        <Field label="Accent word(s) in headline" value={h.hl_accent} onChange={v => set("hero","hl_accent",v)} hint="Must be an exact substring of the Headline" />
        <Field label="Subheadline (h3)" value={h.subheadline} onChange={v => set("hero","subheadline",v)} />
        <Field label="Lead paragraph"  value={h.lead}         onChange={v => set("hero","lead",v)} multiline />
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Badge Pills</label>
          <div className="space-y-1.5">
            {(h.badges || []).map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="text" value={b} onChange={e => setBadge(i, e.target.value)}
                  className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-orange-400 bg-white" />
                <button type="button" onClick={() => removeBadge(i)} className="text-slate-300 hover:text-red-500">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button type="button" onClick={addBadge}
              className="flex items-center gap-1.5 text-xs text-orange-600 hover:text-orange-700 font-semibold mt-1">
              <Plus className="w-3.5 h-3.5" /> Add badge
            </button>
          </div>
        </div>
        <Field label="Box text (hero illustration)" value={h.art_text} onChange={v => set("hero","art_text",v)} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Floating tag 1" value={h.float1} onChange={v => set("hero","float1",v)} />
          <Field label="Floating tag 2" value={h.float2} onChange={v => set("hero","float2",v)} />
        </div>
      </Section>

      {/* Our Story */}
      <Section title="2. Our Story">
        <Field label="Eyebrow" value={s.eyebrow} onChange={v => set("story","eyebrow",v)} />
        <Field label="Heading" value={s.headline} onChange={v => set("story","headline",v)} />
        <Field label="Accent word(s)" value={s.hl_accent} onChange={v => set("story","hl_accent",v)} />
        <Field label="Paragraph 1" value={s.para1} onChange={v => set("story","para1",v)} multiline />
        <Field label="Paragraph 2" value={s.para2} onChange={v => set("story","para2",v)} multiline />
        <Field label="Illustration emoji" value={s.emoji} onChange={v => set("story","emoji",v)} hint="Paste emoji(s) e.g. 🌏📦" />
      </Section>

      {/* What We Do */}
      <Section title="3. What We Do">
        <Field label="Eyebrow" value={w.eyebrow} onChange={v => set("what_we_do","eyebrow",v)} />
        <Field label="Heading" value={w.headline} onChange={v => set("what_we_do","headline",v)} />
        <Field label="Accent word(s)" value={w.hl_accent} onChange={v => set("what_we_do","hl_accent",v)} />
        <Field label="Paragraph 1" value={w.para1} onChange={v => set("what_we_do","para1",v)} multiline />
        <Field label="Paragraph 2" value={w.para2} onChange={v => set("what_we_do","para2",v)} multiline />
        <Field label="Illustration emoji" value={w.emoji} onChange={v => set("what_we_do","emoji",v)} />
      </Section>

      {/* Why Choose Us — 4 feature cards */}
      <Section title="4. Why Choose Us (Feature Cards)">
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Field label="Eyebrow" value={y.eyebrow} onChange={v => set("why_choose","eyebrow",v)} />
          <Field label="Accent word(s)" value={y.hl_accent} onChange={v => set("why_choose","hl_accent",v)} />
        </div>
        <Field label="Heading" value={y.headline} onChange={v => set("why_choose","headline",v)} />
        <Field label="Sub-paragraph" value={y.subtext} onChange={v => set("why_choose","subtext",v)} />
        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-2 mb-1">Feature Cards</p>
        {(y.cards || []).map((card, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2 relative">
            <button type="button" onClick={() => removeCard("why_choose", i)}
              className="absolute top-2 right-2 text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Icon (emoji)" value={card.icon} onChange={v => setCard("why_choose",i,"icon",v)} />
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Color</label>
                <select value={card.variant || "story-card"} onChange={e => setCard("why_choose",i,"variant",e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
                  <option value="story-card">Warm (default)</option>
                  <option value="blue">Blue</option>
                  <option value="green">Green</option>
                  <option value="pink">Pink</option>
                </select>
              </div>
            </div>
            <Field label="Title" value={card.title} onChange={v => setCard("why_choose",i,"title",v)} />
            <Field label="Description" value={card.text} onChange={v => setCard("why_choose",i,"text",v)} />
          </div>
        ))}
        {(y.cards || []).length < 8 && (
          <button type="button" onClick={() => addCard("why_choose", { icon: "⭐", title: "New Feature", text: "Description here.", variant: "blue" })}
            className="flex items-center gap-1.5 text-xs text-orange-600 hover:text-orange-700 font-semibold mt-1">
            <Plus className="w-3.5 h-3.5" /> Add card
          </button>
        )}
      </Section>

      {/* Mission / Vision / Values */}
      <Section title="5. Mission, Vision & Values">
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Field label="Eyebrow" value={m.eyebrow} onChange={v => set("mvv","eyebrow",v)} />
          <Field label="Accent word(s)" value={m.hl_accent} onChange={v => set("mvv","hl_accent",v)} />
        </div>
        <Field label="Heading" value={m.headline} onChange={v => set("mvv","headline",v)} />
        {(m.cards || []).map((card, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2 relative">
            <button type="button" onClick={() => removeCard("mvv", i)}
              className="absolute top-2 right-2 text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Icon" value={card.icon} onChange={v => setCard("mvv",i,"icon",v)} />
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Color</label>
                <select value={card.variant || "story-card"} onChange={e => setCard("mvv",i,"variant",e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white">
                  <option value="story-card">Warm</option>
                  <option value="blue">Blue</option>
                  <option value="green">Green</option>
                  <option value="pink">Pink</option>
                </select>
              </div>
            </div>
            <Field label="Title" value={card.title} onChange={v => setCard("mvv",i,"title",v)} />
            <Field label="Text" value={card.text} onChange={v => setCard("mvv",i,"text",v)} multiline />
          </div>
        ))}
        {(m.cards || []).length < 6 && (
          <button type="button" onClick={() => addCard("mvv", { icon: "⭐", title: "New Card", text: "Description here.", variant: "blue" })}
            className="flex items-center gap-1.5 text-xs text-orange-600 hover:text-orange-700 font-semibold mt-1">
            <Plus className="w-3.5 h-3.5" /> Add card
          </button>
        )}
      </Section>

      {/* Commitment */}
      <Section title="6. Commitment">
        <Field label="Eyebrow" value={c.eyebrow} onChange={v => set("commitment","eyebrow",v)} />
        <Field label="Heading" value={c.headline} onChange={v => set("commitment","headline",v)} />
        <Field label="Accent word(s)" value={c.hl_accent} onChange={v => set("commitment","hl_accent",v)} />
        <Field label="Body text" value={c.text} onChange={v => set("commitment","text",v)} multiline />
        <Field label="Emoji decoration" value={c.emoji} onChange={v => set("commitment","emoji",v)} />
      </Section>

      {/* CTA */}
      <Section title="7. Call to Action">
        <Field label="Heading" value={t.headline} onChange={v => set("cta","headline",v)} />
        <Field label="Accent word(s)" value={t.hl_accent} onChange={v => set("cta","hl_accent",v)} />
        <Field label="Sub-paragraph" value={t.subtext} onChange={v => set("cta","subtext",v)} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Button text" value={t.button_text} onChange={v => set("cta","button_text",v)} />
          <Field label="Button URL" value={t.button_url} onChange={v => set("cta","button_url",v)} />
        </div>
      </Section>
    </div>
  );
}
